import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileCompletionProgress = ({ onSectionChange }) => {
  const navigate = useNavigate();
  const completionData = {
    overall: 70,
    sections: [
      { name: 'Basic Information', progress: 100, icon: 'User', color: 'bg-green-500' },
      { name: 'Wedding Details', progress: 85, icon: 'Calendar', color: 'bg-green-500' },
      { name: 'Preferences', progress: 60, icon: 'Settings', color: 'bg-yellow-500' },
      { name: 'Budget Planning', progress: 45, icon: 'DollarSign', color: 'bg-yellow-500' },
      { name: 'Vendor Selections', progress: 30, icon: 'Users', color: 'bg-red-500' },
      { name: 'Guest List', progress: 90, icon: 'UserCheck', color: 'bg-green-500' }
    ]
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'from-green-500 to-green-600';
    if (progress >= 60) return 'from-yellow-500 to-yellow-600';
    return 'from-red-500 to-red-600';
  };

  const getStatusText = (progress) => {
    if (progress >= 80) return 'Complete';
    if (progress >= 60) return 'Good Progress';
    return 'Needs Attention';
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-2">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-text-primary">
          Profile Completion
        </h3>
        <div className="text-right sm:text-left">
          <div className="text-xl md:text-2xl font-bold text-primary">{completionData.overall}%</div>
          <div className="text-text-secondary text-sm">Complete</div>
        </div>
      </div>

      {/* Overall Progress Circle */}
      <div className="flex items-center justify-center mb-4 md:mb-6">
        <div className="relative w-24 h-24 md:w-32 md:h-32">
          <svg className="w-24 h-24 md:w-32 md:h-32 transform -rotate-90" viewBox="0 0 120 120">
            <circle
              cx="60"
              cy="60"
              r="54"
              stroke="#e5e7eb"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="60"
              cy="60"
              r="54"
              stroke="url(#gradient)"
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 54}
              strokeDashoffset={2 * Math.PI * 54 * (1 - completionData.overall / 100)}
              className="transition-all duration-1000"
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8B1538" />
                <stop offset="100%" stopColor="#D4AF37" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-lg md:text-2xl font-bold text-primary">{completionData.overall}%</div>
              <div className="text-text-secondary text-xs md:text-sm">Complete</div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Progress */}
      <div className="space-y-3 md:space-y-4">
        {completionData.sections.map((section, index) => (
          <div key={index} className="flex items-center space-x-3 md:space-x-4">
            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg ${section.color} flex items-center justify-center flex-shrink-0`}>
              <Icon name={section.icon} size={16} className="text-white md:w-5 md:h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 gap-1">
                <span className="text-xs md:text-sm font-medium text-text-primary truncate">{section.name}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-text-secondary">{section.progress}%</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    section.progress >= 80 ? 'bg-green-100 text-green-600' :
                    section.progress >= 60 ? 'bg-yellow-100 text-yellow-600': 'bg-red-100 text-red-600'
                  }`}>
                    {getStatusText(section.progress)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full bg-gradient-to-r ${getProgressColor(section.progress)} transition-all duration-500`}
                  style={{ width: `${section.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Next Steps */}
      <div className="mt-4 md:mt-6 p-3 md:p-4 bg-primary-50 rounded-lg">
        <h4 className="font-medium text-text-primary mb-2 text-sm md:text-base">Next Steps to Complete Your Profile</h4>
        <ul className="text-xs md:text-sm text-text-secondary space-y-1">
          <li className="flex items-start space-x-2">
            <Icon name="ArrowRight" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Complete your budget planning details</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="ArrowRight" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Add more vendor selections</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="ArrowRight" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <span>Update your wedding preferences</span>
          </li>
        </ul>
        <Button
          variant="primary"
          size="sm"
          className="mt-3 w-full sm:w-auto"
          iconName="Settings"
          onClick={() => onSectionChange?.('profile')}
        >
          Update Profile
        </Button>
      </div>
    </div>
  );
};

export default ProfileCompletionProgress;